import React, { useState, useEffect } from 'react';
import { vehicleService } from '../services/api';

interface Vehicle {
  vehicleNumber: string;
  ownerName: string;
  ownerType: string;
  ownerMobile: string;
  ownerEmail?: string;
  ownerPhoto?: string;
  vehicleType?: string;
  shopkeeperName?: string;
  shopkeeperMobile?: string;
}

const Vehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async (search?: string) => {
    try {
      setLoading(true);
      const response = await vehicleService.getAll(search);
      if (response.data?.success) {
        setVehicles(response.data.data.vehicles || []);
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadVehicles(searchTerm);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() === '') {
      loadVehicles();
    }
  };

  const getOwnerTypeBadgeColor = (ownerType: string) => {
    if (ownerType.includes('Shopkeeper')) {
      return 'bg-blue-100 text-blue-800';
    } else if (ownerType.includes('Rental Owner')) {
      return 'bg-purple-100 text-purple-800';
    } else if (ownerType.includes('Employee')) {
      return 'bg-green-100 text-green-800';
    } else if (ownerType.includes('Staff')) {
      return 'bg-orange-100 text-orange-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vehicles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Vehicle Management</h1>
        <div className="text-sm text-gray-600">
          Total Vehicles: <span className="font-semibold text-primary-500">{vehicles.length}</span>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by vehicle number, owner name, mobile, or email..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
          >
            <span>🔍</span> Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                loadVehicles();
              }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-all"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Vehicles Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {vehicles.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-6xl mb-4">🚗</div>
            <p className="text-gray-600 text-lg">
              {searchTerm ? 'No vehicles found matching your search' : 'No vehicles registered yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Vehicle Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Vehicle Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Owner Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Related Shopkeeper
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.map((vehicle, index) => (
                  <tr key={`${vehicle.vehicleNumber}-${index}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900">{vehicle.vehicleNumber}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.vehicleType ? (
                        <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-semibold">
                          {vehicle.vehicleType}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {vehicle.ownerPhoto ? (
                          <img
                            src={vehicle.ownerPhoto}
                            alt={vehicle.ownerName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold">
                            {vehicle.ownerName?.charAt(0).toUpperCase() || '?'}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{vehicle.ownerName}</div>
                          {vehicle.ownerEmail && (
                            <div className="text-xs text-gray-500">{vehicle.ownerEmail}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getOwnerTypeBadgeColor(vehicle.ownerType)}`}>
                        {vehicle.ownerType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vehicle.ownerMobile}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.shopkeeperName ? (
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{vehicle.shopkeeperName}</div>
                          <div className="text-xs text-gray-500">{vehicle.shopkeeperMobile}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Vehicles;
